import {ApplicationRef, ComponentFactoryResolver, Injectable, Injector} from '@angular/core';
import {ExcCoreModule} from 'exc-core-v2';
import {ExcVideoViewerComponent} from '../misc/exc-video-viewer/exc-video-viewer.component';

@Injectable({providedIn: 'root'})
export class VideoService {
  private excRestServiceCore = ExcCoreModule.Services().ExcRest;

  private readonly mimeTypeMap: {[ext: string]: string} = {
    mp4: 'video/mp4',
    webm: 'video/webm',
    ogg: 'video/ogg',
    mov: 'video/mp4'
  };

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector
  ) {}

  openVideo(item: any): void {
    const videoWindow = window.open(
      '',
      'VideoViewer',
      'width=900,height=560,scrollbars=no,resizable=yes'
    );

    if (!videoWindow) {
      return;
    }

    const videoUrl = this.excRestServiceCore.computeUrl('LibraryStream', {libID: item.libID});
    const ext = (item.libExtension || '').toLowerCase();
    const mimeType = item.libMimeType?.includes('video/')
      ? item.libMimeType
      : this.mimeTypeMap[ext] || 'video/mp4';

    videoWindow.document.title = item.libDescription || 'Video';
    videoWindow.document.body.innerHTML = '';

    this.copyStylesToWindow(videoWindow).then(() => {

      const wrapper = videoWindow.document.createElement('div');
      wrapper.style.padding = '16px';
      wrapper.style.background = '#111';
      wrapper.style.height = '100vh';
      wrapper.style.boxSizing = 'border-box';
      wrapper.style.display = 'flex';
      wrapper.style.flexDirection = 'column';

      const title = videoWindow.document.createElement('div');
      title.innerText = item.libDescription || 'Video';
      title.style.color = '#fff';
      title.style.marginBottom = '12px';
      title.style.fontFamily = 'Arial, sans-serif';
      title.style.fontSize = '14px';

      const playerContainer = videoWindow.document.createElement('div');
      playerContainer.style.flex = '1';
      playerContainer.style.display = 'flex';
      playerContainer.style.flexDirection = 'column';
      playerContainer.style.overflow = 'hidden';

      wrapper.appendChild(title);
      wrapper.appendChild(playerContainer);
      videoWindow.document.body.appendChild(wrapper);

      const componentFactory = this.componentFactoryResolver.resolveComponentFactory(ExcVideoViewerComponent);
      const componentRef = componentFactory.create(this.injector);

      componentRef.instance.src = videoUrl;
      componentRef.instance.mimeType = mimeType;

      this.appRef.attachView(componentRef.hostView);
      playerContainer.appendChild(componentRef.location.nativeElement);
      componentRef.changeDetectorRef.detectChanges();

      setTimeout(() => {
        componentRef.instance.initPlayer();

        setTimeout(() => {
          const sprite = document.querySelector('#sprite-plyr');
          if (sprite) {
            const existing = videoWindow.document.querySelector('#sprite-plyr');
            if (!existing) {
              const clone = sprite.cloneNode(true) as Element;
              videoWindow.document.body.insertBefore(clone, videoWindow.document.body.firstChild);
            }
          }
        }, 100);
      }, 0);

      videoWindow.onbeforeunload = () => {
        this.appRef.detachView(componentRef.hostView);
        componentRef.destroy();
      };
    });
  }

  isVideo(item: any): boolean {
    const videoExtensions = ['mp4', 'webm', 'ogg', 'mov'];
    const ext = (item.libExtension || '').toLowerCase();

    return item.libMimeType?.includes('video/') || videoExtensions.includes(ext);
  }

  private copyStylesToWindow(targetWindow: Window): Promise<void> {
    const styleNodes = Array.from(document.querySelectorAll('style'));
    styleNodes.forEach((styleNode: Element) => {
      targetWindow.document.head.appendChild(styleNode.cloneNode(true));
    });

    const linkNodes = Array.from(document.querySelectorAll('link[rel="stylesheet"]')) as HTMLLinkElement[];

    const responsiveStyle = targetWindow.document.createElement('style');
    responsiveStyle.textContent = `
      html, body { margin: 0; padding: 0; height: 100%; background: #111; }
      .plyr { width: 100%; }
      .plyr--video { width: 100%; }
      video { max-height: calc(100vh - 60px); width: 100% !important; object-fit: contain; }
    `;
    targetWindow.document.head.appendChild(responsiveStyle);

    return Promise.all(linkNodes.map((linkNode: HTMLLinkElement) => {
      return new Promise<void>((resolve) => {
        const link = targetWindow.document.createElement('link');
        link.rel = 'stylesheet';
        link.href = linkNode.href;
        link.onload = () => resolve();
        link.onerror = () => resolve();

        targetWindow.document.head.appendChild(link);
      });
    })).then(() => undefined);
  }
}
